import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { Toggle } from "@/components/Toggle";
import { Button } from "@/components/button";
import { Translate, useTrans } from "@/translations";

import React, { useCallback, useEffect } from "react";

import { remove } from "lodash";
import DashboardTileType, {
  MWMDashboardTileType,
  REDashboardTileType,
} from "server/utils/dashboardTileType.js";

interface Props {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}

const getTileTitleKey = (tileType: DashboardTileType) => {
  switch (tileType) {
    case "KEY_NUMBERS":
      return "circularityLabels:keyNumbers";
    case "KEY_NUMBERS_PER_CATEGORY":
      return "circularityLabels:keyNumbersPerCategory";
    case "WASTE_TYPES_IN_USE":
      return "circularityLabels:wasteTypeCategoriesInUse";
    case "RECYCLING_RATE_OVER_TIME":
      return "circularityLabels:recyclingRateOverTime";
    case "DEVELOPMENT_IN_WASTE_TYPES":
      return "circularityLabels:developmentInWasteTypes";
    case "WASTE_CONTRIBUTION":
      return "statisticsLabels:wasteContributions";
    case "WASTE_TYPE_DISTRIBUTION":
      return "circularityLabels:wasteTypeDistribution";
    case "INFRASTRUCTURE_SUMMARY":
      return "statisticsLabels:infrastructureSummary";
    default:
      throw new Error(`Unknown tile type: ${tileType}`);
  }
};

export const Dashboard: React.FC<Props> = ({
  hasUnsavedChanges,
  setHasUnsavedChanges,
}) => {
  const { t } = useTrans();
  const { isMWM } = useConfig();
  const { data, refetch } = trpc.config.getDashboardTiles.useQuery();
  const [tiles, setTiles] = React.useState<
    { type: DashboardTileType; visible: boolean }[]
  >([]);
  const { mutate, isPending: isLoading } =
    trpc.config.updateDashboardTiles.useMutation({
      onSuccess: () => {
        refetch();
      },
    });

  useEffect(() => {
    const hasDifference = tiles.find((t) => {
      const originalTileVisible = data?.find(
        (d) => d.tileType === t.type,
      )?.visible;

      return t.visible !== originalTileVisible;
    });

    setHasUnsavedChanges(!!hasDifference);
  }, [tiles, data, setHasUnsavedChanges]);

  const findTile = useCallback(
    (tileType: DashboardTileType) => {
      return (
        tiles.find((t) => t.type === tileType) ??
        data?.find((tile) => tile.tileType === tileType)
      );
    },
    [tiles, data],
  );

  const onChange = useCallback(
    (tileType: DashboardTileType, checked: boolean) => {
      setTiles((prev) => [
        ...remove(prev, ({ type }) => type !== tileType),
        {
          type: tileType,
          visible: checked,
        },
      ]);
    },
    [setTiles],
  );

  const onSave = useCallback(() => {
    mutate(
      tiles.map(({ type, visible }) => ({
        tileType: type,
        visible,
      })),
    );
  }, [tiles, mutate]);

  const values = isMWM
    ? (Object.values(MWMDashboardTileType) as DashboardTileType[])
    : (Object.values(REDashboardTileType) as DashboardTileType[]);

  return (
    <div>
      <div className="mb-4">
        <ul className="ml-2 list-inside list-disc">
          <Translate i18nKey="configLabels:dashboardConfigDescription">
            <li />
            <li />
            <li />
          </Translate>
        </ul>
      </div>
      {values.map((tileType) => {
        return (
          <Row
            key={tileType}
            title={t(getTileTitleKey(tileType))}
            onChange={(checked) => onChange(tileType, checked)}
            isChecked={findTile(tileType)?.visible ?? true}
          />
        );
      })}
      <hr className="-mx-4 mt-4" />
      <Button
        label={t("actions:save")}
        onClick={onSave}
        loading={isLoading}
        disabled={!hasUnsavedChanges}
        className="my-4"
      />
    </div>
  );
};

const Row = ({
  title,
  isChecked,
  onChange,
}: {
  title: string;
  isChecked: boolean;
  onChange: (checked: boolean) => void;
}) => {
  return (
    <div className="flex items-center py-2">
      <Toggle checked={isChecked} onChange={onChange} disabled={false} />
      <span className="ml-2 text-sm">{title}</span>
    </div>
  );
};
